import {type BucketedLine} from '../../types';
import {
  applyBackendLinesDelta,
  applyDelta,
  applyGroupedBackendLinesDelta,
  applyGroupedDelta,
} from './applyDeltas';
import {applyPinnedSubQuery} from './applyPinnedSubQuery';
import {
  doBackendLinesApiQuery,
  doBackendLinesApiQueryByInternalId,
  doBucketedApiQuery,
  doBucketedApiQueryByInternalId,
  doGroupedBackendLinesApiQuery,
  doGroupedBucketedApiQuery,
  doGroupedBucketedApiQueryByInternalId,
} from './execution';
import {postProcessGroupedRuns, postProcessRuns} from './postProcessRuns';
import {type QueryVars} from './queryVars';
import {
  type BackendLinesQueryResult,
  type BackendLinesQueryRuns,
  type BackendLinesQueryRunsDeltas,
  type BaseQueryBehavior,
  type BucketedQueryResult,
  type BucketedQueryRuns,
  type BucketedQueryRunsDeltas,
  type GroupedBackendLinesQueryResult,
  type GroupedBackendLinesQueryRuns,
  type GroupedBucketedQueryResult,
  type GroupedBucketedQueryRuns,
} from './types';
import {extractDeltasInfo, isAggregatedRuns} from './utils';

type BackendLinesQueryBehaviorType = BaseQueryBehavior<
  BackendLinesQueryResult,
  BackendLinesQueryRuns
>;

type GroupedBackendLinesQueryBehaviorType = BaseQueryBehavior<
  GroupedBackendLinesQueryResult,
  GroupedBackendLinesQueryRuns
>;

function applyBackendLinesDeltaFn(vars: {
  currentRunsById: Record<string, BucketedLine>;
  runs: BackendLinesQueryRuns | null | undefined;
  specs: QueryVars['bucketedHistorySpecs'];
}) {
  const {deltas, order} = extractDeltasInfo(vars.runs);
  return applyBackendLinesDelta({
    currentRunsById: vars.currentRunsById,
    delta: deltas,
    order,
  });
}

function applyBucketedDeltaFn(vars: {
  currentRunsById: Record<string, BucketedLine>;
  runs: BucketedQueryRuns | null | undefined;
  specs: QueryVars['bucketedHistorySpecs'];
}) {
  const {deltas, order} = extractDeltasInfo(vars.runs);
  return applyDelta({
    currentRunsById: vars.currentRunsById,
    order,
    delta: deltas,
    specs: vars.specs,
  });
}

function unpackBackendLinesRuns(result: BackendLinesQueryResult) {
  return result?.data?.project?.runs;
}

function unpackBucketedRuns(result: BucketedQueryResult) {
  return result?.data?.project?.runs;
}

function unpackGroupedBackendLinesRuns(result: GroupedBackendLinesQueryResult) {
  return result?.data?.project?.groupedRuns;
}

function unpackGroupedBucketedRuns(result: GroupedBucketedQueryResult) {
  return result?.data?.project?.groupedRuns;
}

export const BackendLinesQueryBehavior: BackendLinesQueryBehaviorType = {
  queryName: 'BackendLinesQueryBehavior',
  query: doBackendLinesApiQuery,
  applyPinnedSubQuery,
  unpack: unpackBackendLinesRuns,
  postProcess: null,
  applyDeltaFn: applyBackendLinesDeltaFn,
};

export const BackendLinesIdQueryBehavior: BackendLinesQueryBehaviorType = {
  queryName: 'BackendLinesIdQueryBehavior',
  query: doBackendLinesApiQueryByInternalId,
  applyPinnedSubQuery,
  unpack: unpackBackendLinesRuns,
  postProcess: null,
  applyDeltaFn: applyBackendLinesDeltaFn,
};

export const GroupedBackendLinesQueryBehavior: GroupedBackendLinesQueryBehaviorType =
  {
    queryName: 'GroupedBackendLinesQueryBehavior',
    query: doGroupedBackendLinesApiQuery,
    applyPinnedSubQuery: null,
    unpack: unpackGroupedBackendLinesRuns,
    postProcess: null,
    applyDeltaFn: (vars: {
      currentRunsById: Record<string, BucketedLine>;
      runs: GroupedBackendLinesQueryRuns | null | undefined;
      specs: QueryVars['bucketedHistorySpecs'];
    }) => {
      /**
       * The Project.GroupedRuns resolver returns GroupedRuns if it's a supported use case or Runs if it's not. If it is, we can use the grouped delta function. Otherwise we fall back to the same behavior as the non-grouped BackendLinesQueryBehavior (applyBackendLinesDelta), since it will have returned non-aggregated line data.
       */
      const runs = vars.runs;
      const order = vars.runs?.deltas?.order ?? [];
      const delta = vars.runs?.deltas?.delta ?? [];

      if (isAggregatedRuns(runs)) {
        return applyGroupedBackendLinesDelta({
          currentRunsById: vars.currentRunsById,
          order,
          delta,
        });
      }

      return applyBackendLinesDelta({
        currentRunsById: vars.currentRunsById,
        order,
        delta: delta as BackendLinesQueryRunsDeltas,
      });
    },
  };

type BucketedQueryBehaviorType = BaseQueryBehavior<
  BucketedQueryResult,
  BucketedQueryRuns
>;

export const BucketedQueryBehavior: BucketedQueryBehaviorType = {
  queryName: 'BucketedQueryBehavior',
  query: doBucketedApiQuery,
  applyPinnedSubQuery,
  unpack: unpackBucketedRuns,
  postProcess: postProcessRuns,
  applyDeltaFn: applyBucketedDeltaFn,
};

export const BucketedQueryWithInternalIdBehavior: BucketedQueryBehaviorType = {
  queryName: 'BucketedQueryWithInternalIdBehavior',
  query: doBucketedApiQueryByInternalId,
  applyPinnedSubQuery,
  unpack: unpackBucketedRuns,
  postProcess: postProcessRuns,
  applyDeltaFn: applyBucketedDeltaFn,
};

type GroupedQueryBehaviorType = BaseQueryBehavior<
  GroupedBucketedQueryResult,
  GroupedBucketedQueryRuns
>;

export const GroupedBucketedQueryBehavior: GroupedQueryBehaviorType = {
  queryName: 'GroupedBucketedQueryBehavior',
  query: doGroupedBucketedApiQuery,
  applyPinnedSubQuery: null,
  unpack: unpackGroupedBucketedRuns,
  postProcess: (runs: GroupedBucketedQueryRuns | null | undefined) => {
    if (runs?.__typename === 'GroupedRunConnection') {
      return postProcessGroupedRuns(runs);
    }
    return postProcessRuns(runs as BucketedQueryRuns);
  },
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: GroupedBucketedQueryRuns | null | undefined;
    specs: QueryVars['bucketedHistorySpecs'];
  }) => {
    const runs = vars.runs;
    if (isAggregatedRuns(runs)) {
      return applyGroupedDelta({
        currentRunsById: vars.currentRunsById,
        order: runs.deltas?.order ?? [],
        delta: runs.deltas?.delta ?? [],
        specs: vars.specs,
      });
    }

    // After the isAggregatedRuns check above, we know this is a RunConnection (not GroupedRunConnection),
    // but TypeScript can't narrow runs through the type guard, so we cast the delta type.
    const delta = (runs?.deltas?.delta ?? []) as BucketedQueryRunsDeltas;
    const order = runs?.deltas?.order ?? [];
    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order,
      delta,
      specs: vars.specs,
    });
  },
};

export const GroupedBucketedQueryWithInternalIdBehavior: GroupedQueryBehaviorType =
  {
    queryName: 'GroupedBucketedQueryWithInternalIdBehavior',
    query: doGroupedBucketedApiQueryByInternalId,
    applyPinnedSubQuery: null,
    unpack: unpackGroupedBucketedRuns,
    postProcess: (runs: GroupedBucketedQueryRuns | null | undefined) => {
      if (runs?.__typename === 'GroupedRunConnection') {
        return postProcessGroupedRuns(runs);
      }
      return postProcessRuns(runs as BucketedQueryRuns);
    },
    applyDeltaFn: (vars: {
      currentRunsById: Record<string, BucketedLine>;
      runs: GroupedBucketedQueryRuns | null | undefined;
      specs: QueryVars['bucketedHistorySpecs'];
    }) => {
      const runs = vars.runs;
      if (isAggregatedRuns(runs)) {
        return applyGroupedDelta({
          currentRunsById: vars.currentRunsById,
          order: runs.deltas?.order ?? [],
          delta: runs.deltas?.delta ?? [],
          specs: vars.specs,
        });
      }

      // After the isAggregatedRuns check above, we know this is a RunConnection (not GroupedRunConnection),
      // but TypeScript can't narrow runs through the type guard, so we cast the delta type.
      const delta = (runs?.deltas?.delta ?? []) as BucketedQueryRunsDeltas;
      const order = runs?.deltas?.order ?? [];
      return applyDelta({
        currentRunsById: vars.currentRunsById,
        order,
        delta,
        specs: vars.specs,
      });
    },
  };
